import { getProjects, getProjectDisplayName, type Project } from '@data/projects';
import type { UserRole } from '@services/auth';
import * as calendarAPI from '@services/calendar';
import type { CalendarEventResponse } from '@services/calendar';
import { getStoredCompanyScopeMode } from '@services/companies';

export type CalendarEventType = 'task' | 'project_milestone' | 'site_visit' | 'completion';

export type CalendarEvent = {
	id: string;
	title: string;
	date: Date;
	type: CalendarEventType;
	projectId?: string;
	projectName?: string;
	projectCompanyName?: string;
	taskId?: string;
	siteVisitId?: string;
	color: string;
	status?: string;
	assignedGardenerId?: string;
};

const EVENT_COLORS: Record<CalendarEventType, string> = {
	task: '#198754', // green
	project_milestone: '#0d6efd', // blue
	site_visit: '#fd7e14', // orange
	completion: '#6f42c1' // purple
};

// Map backend calendar event to frontend format
function mapBackendEventToFrontend(backendEvent: CalendarEventResponse): CalendarEvent {
	return {
		id: backendEvent.id,
		title: backendEvent.title,
		date: new Date(backendEvent.date),
		type: backendEvent.type,
		projectId: backendEvent.project_id ? String(backendEvent.project_id) : undefined,
		projectName: backendEvent.project_name,
		projectCompanyName: backendEvent.project_company_name?.trim() || undefined,
		taskId: backendEvent.task_id ? String(backendEvent.task_id) : undefined,
		siteVisitId: backendEvent.site_visit_id ? String(backendEvent.site_visit_id) : undefined,
		// Always use frontend palette so legend and dots stay consistent
		color: EVENT_COLORS[backendEvent.type],
		status: backendEvent.status,
		assignedGardenerId: backendEvent.assigned_gardener_id ? String(backendEvent.assigned_gardener_id) : undefined
	};
}

// Role-based project assignments (from Dashboard.tsx)
const roleProjectAssignments: Record<UserRole, string[]> = {
	admin: [], // admins see all
	company: [], // companies see all
	gardener: ['pr2', 'pr3', 'pr4'],
	seeker: ['pr1', 'pr5']
};

// Role-based task assignments (from Dashboard.tsx)
const roleTaskAssignments: Partial<Record<UserRole, string[]>> = {
	gardener: ['t1', 't2', 't5', 't6', 't7', 't8', 't10']
};

const gardenerOfferAssignments: Partial<Record<UserRole, string>> = {
	gardener: 'g1'
};

const companyGardenerIds = ['g1', 'g2'];

// Get calendar events (backend only; no mock/demo fallback)
export async function getCalendarEvents(
	userRole: UserRole,
	userId?: string,
	scope?: 'self' | 'my_projects' | 'company' | 'all_companies'
): Promise<CalendarEvent[]> {
	if (!userId) return [];
	const userIdNum = parseInt(userId, 10);
	if (isNaN(userIdNum)) return [];

	// Determine effective scope when not explicitly provided
	let effectiveScope: 'self' | 'my_projects' | 'company' | 'all_companies';
	if (scope) {
		effectiveScope = scope;
	} else if (userRole === 'company') {
		effectiveScope = 'company';
	} else if (userRole === 'gardener') {
		effectiveScope = 'my_projects';
	} else {
		effectiveScope = 'self';
	}

	try {
		const backendEvents = await calendarAPI.getCalendarEvents({
			user_id: userIdNum,
			role: userRole,
			scope: effectiveScope
		});
		const mappedEvents = backendEvents.map(mapBackendEventToFrontend);

		// Enrich with Gantt chart tasks only for gardeners (real projects from backend).
		// Important: in "Only me" mode (`scope: self`) we already get the correct DB tasks
		// from the backend calendar query. Adding ganttData can create dots that are
		// out of sync after server-side rescheduling (PTO/company working-days),
		// which leads to wrong/un-clickable events.
		if (userRole === 'gardener' && effectiveScope !== 'self') {
			let userProjects: Project[] = [];
			try {
				const scopeMode = getStoredCompanyScopeMode();
				userProjects = await getProjects({
					requester_user_id: userIdNum,
					company_scope: scopeMode === 'all_companies' ? 'all_companies' : 'company'
				});

				// Backend tasks are the source of truth for the calendar dots.
				// The enrichment below adds "gantt-" dots for Gantt tasks that might not exist in DB.
				// For projects where DB tasks exist, we must avoid duplicates.
				//
				// We therefore track what the backend already shows for this user:
				// if backend already has a task dot for (project, date), we do not add a gantt dot.
				const backendTaskDateProjectSet = new Set(
					mappedEvents
						.filter(e => e.type === 'task' && !!e.taskId)
						.map(e => `${e.projectId ?? ''}::${e.date.toDateString()}`)
				);

				userProjects.forEach(project => {
					if (project.ganttData && project.ganttData.length > 0) {
						project.ganttData.forEach(ganttTask => {
							if (ganttTask.status === 'completed') return;
							const taskGardenerIds = ganttTask.gardenerIds || (ganttTask.gardenerId ? [ganttTask.gardenerId] : []);
							const userIdStr = String(userId);
							if (!taskGardenerIds.includes(userIdStr)) return;

							const taskDate = new Date(ganttTask.startDate);
							const backendKey = `${project.id}::${taskDate.toDateString()}`;
							if (backendTaskDateProjectSet.has(backendKey)) return;

							// Only provide a taskId to enable click-through.
							// For non-db gantt ids (e.g. plan-step-1), click would fail because it's not a DB id.
							const maybeTaskId = (typeof ganttTask.id === 'string' && ganttTask.id.startsWith('db-'))
								? ganttTask.id.replace('db-', '')
								: undefined;
							// If we can't map this gantt entry to a real DB task, don't add it to the calendar.
							// Otherwise it becomes a non-clickable dot (what we want to get rid of).
							if (!maybeTaskId) return;
							mappedEvents.push({
								id: `gantt-${project.id}-${ganttTask.id}`,
								title: ganttTask.name,
								date: taskDate,
								type: 'task',
								projectId: project.id,
								projectName: getProjectDisplayName(project),
								taskId: maybeTaskId,
								color: EVENT_COLORS.task,
								status: ganttTask.status,
								assignedGardenerId: ganttTask.gardenerIds?.[0] || ganttTask.gardenerId
							});
						});
					}
				});
			} catch (err) {
				console.error('Failed to load projects for Gantt tasks:', err);
			}
		}

		// Sort by date
		return mappedEvents.sort((a, b) => a.date.getTime() - b.date.getTime());
	} catch (err) {
		console.error('Failed to load calendar events from API:', err);
		return [];
	}
}

export function getEventsForDate(events: CalendarEvent[], date: Date): CalendarEvent[] {
	if (!Array.isArray(events)) {
		return [];
	}
	const dateStr = date.toDateString();
	return events.filter(event => event.date.toDateString() === dateStr);
}

export function hasEventsOnDate(events: CalendarEvent[], date: Date): boolean {
	if (!Array.isArray(events)) {
		return false;
	}
	return getEventsForDate(events, date).length > 0;
}

export { EVENT_COLORS };

